import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Loader2, Check, Calendar, Rocket, AlertTriangle, ArrowRight, Sparkles,
} from "lucide-react";

/**
 * Tenant upgrade hub.
 * - Shows trial status header (active/expired)
 * - Lists every paid plan from /api/admin/plans (or /api/saas/plans)
 * - "Upgrade" CTA → POST /admin/upgrade (Razorpay charge MOCKED)
 * - "Schedule a demo" mini-form → POST /admin/demo/schedule
 */
export default function AdminUpgrade() {
  const nav = useNavigate();
  const [trial, setTrial] = useState(null);
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [picked, setPicked] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [demoForm, setDemoForm] = useState({ preferred_at: "", notes: "" });
  const [demoSent, setDemoSent] = useState(false);

  const refresh = async () => {
    try {
      const [t, p] = await Promise.all([
        api.get("/admin/trial-status"),
        api.get("/features/plans").catch(() => ({ data: [] })),
      ]);
      setTrial(t.data);
      const list = Array.isArray(p.data) ? p.data : (p.data?.items || []);
      setPlans(list.filter((pl) => pl.slug !== "trial"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load");
    }
  };
  useEffect(() => { refresh(); }, []);

  const upgrade = async (planSlug) => {
    setUpgrading(true);
    try {
      const { data } = await api.post("/admin/upgrade", { plan_slug: planSlug, billing_cycle: cycle });
      toast.success(`Welcome to ${data.plan?.name || planSlug}!`);
      // Bust the TrialBanner + any other in-flight trial-status consumers
      window.dispatchEvent(new Event("trial-status-invalidate"));
      // Reload tenant context + trial status to clear the banner
      await refresh();
      setTimeout(() => nav("/admin"), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upgrade failed");
    } finally { setUpgrading(false); }
  };

  const scheduleDemo = async () => {
    try {
      await api.post("/admin/demo/schedule", demoForm);
      setDemoSent(true);
      toast.success("Demo request sent — we'll be in touch shortly");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send request");
    }
  };

  if (!trial) {
    return <div className="p-10 text-center text-sm text-[hsl(var(--kb-muted))]"><Loader2 size={18} className="animate-spin inline" /></div>;
  }

  const isExpired = trial.is_expired;
  const isTrial = trial.is_trial;
  const days = trial.days_remaining;

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="admin-upgrade-page">
      <div>
        <div className="overline mb-1">Subscription</div>
        <h1 className="font-display text-3xl md:text-4xl flex items-center gap-3">
          {isExpired ? <AlertTriangle size={28} className="text-amber-600" /> : <Rocket size={28} className="text-[hsl(var(--kb-primary))]" />}
          {isExpired ? "Trial ended — pick a plan to keep going" : isTrial ? "Upgrade to a paid plan" : "Manage your subscription"}
        </h1>
        <p className="text-sm text-[hsl(var(--kb-muted))] mt-2 max-w-2xl">
          {isExpired ? (
            <>Your storefront is in read-only mode. Customers can browse but cannot check out, and OTP delivery and AI tools are paused. Upgrade to reactivate everything instantly.</>
          ) : isTrial ? (
            <>You have <b>{days?.toFixed(1)} day{days === 1 ? "" : "s"}</b> left in your trial. Upgrade any time to keep selling beyond day 3.</>
          ) : (
            <>You&apos;re on a paid plan. Switch tiers or update your billing cycle below.</>
          )}
        </p>
      </div>

      {/* Cycle toggle */}
      <div className="inline-flex border border-[hsl(var(--kb-border))]" data-testid="cycle-toggle">
        {["monthly", "yearly"].map((c) => (
          <button
            key={c}
            onClick={() => setCycle(c)}
            className={`px-4 py-2 text-sm capitalize ${cycle === c ? "bg-[hsl(var(--kb-primary))] text-white" : "bg-white"}`}
            data-testid={`cycle-${c}`}
          >
            {c} {c === "yearly" && <span className="text-[10px] opacity-70 ml-1">(save 17%)</span>}
          </button>
        ))}
      </div>

      {/* Plans */}
      <div className="grid md:grid-cols-3 gap-4" data-testid="plans-grid">
        {!plans.length ? (
          <div className="md:col-span-3 p-8 text-center text-sm text-[hsl(var(--kb-muted))] border border-dashed border-[hsl(var(--kb-border))]">
            No plans configured yet — the platform admin needs to publish plans first.
          </div>
        ) : plans.map((p) => {
          const price = cycle === "yearly" ? (p.price_yearly || p.price_monthly * 10) : p.price_monthly;
          return (
            <div key={p.slug} className={`border p-5 flex flex-col bg-white ${picked === p.slug ? "border-[hsl(var(--kb-primary))] ring-2 ring-[hsl(var(--kb-primary))]/20" : "border-[hsl(var(--kb-border))]"}`} data-testid={`plan-${p.slug}`}>
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="font-display text-xl">{p.name}</h3>
                {p.is_popular && <span className="text-[10px] bg-[hsl(var(--kb-primary))] text-white px-2 py-0.5">POPULAR</span>}
              </div>
              <div className="font-display text-3xl mb-1">₹{price?.toLocaleString?.()}</div>
              <div className="text-xs text-[hsl(var(--kb-muted))] mb-4">per {cycle === "yearly" ? "year" : "month"}</div>
              <p className="text-sm text-[hsl(var(--kb-muted))] flex-1 mb-4">{p.description || p.tagline || ""}</p>
              <ul className="space-y-1.5 text-xs mb-4">
                {(p.features || []).slice(0, 6).map((f, i) => (
                  <li key={i} className="flex items-start gap-2"><Check size={12} className="text-green-600 mt-0.5 shrink-0" /> {typeof f === "string" ? f : f.label || f.name}</li>
                ))}
              </ul>
              <button
                type="button"
                disabled={upgrading}
                onClick={() => { setPicked(p.slug); upgrade(p.slug); }}
                className="kb-btn-primary w-full inline-flex items-center justify-center gap-2"
                data-testid={`upgrade-${p.slug}`}
              >
                {upgrading && picked === p.slug ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                Upgrade now (mock)
              </button>
            </div>
          );
        })}
      </div>

      <p className="text-[10px] text-[hsl(var(--kb-muted))] text-center" data-testid="mock-disclaimer">
        Payment is currently MOCKED — clicking Upgrade instantly activates the plan. Razorpay live integration ships next.
      </p>

      {/* Schedule demo */}
      <section className="bg-[hsl(var(--kb-surface-alt))] border border-[hsl(var(--kb-border))] p-6 md:p-8 grid md:grid-cols-[1fr_auto] gap-4 items-center" data-testid="schedule-demo">
        <div>
          <div className="text-xs uppercase tracking-wider text-[hsl(var(--kb-muted))] mb-2 flex items-center gap-2"><Calendar size={12} /> Not ready yet?</div>
          <h2 className="font-display text-2xl mb-2">Talk to us before you decide.</h2>
          <p className="text-sm text-[hsl(var(--kb-muted))]">
            We&apos;ll walk you through every feature live and help you migrate your existing catalog. 15 minutes, on your schedule.
          </p>
        </div>
        {demoSent ? (
          <div className="text-sm text-green-700 inline-flex items-center gap-2" data-testid="demo-sent"><Check size={14} /> Request sent</div>
        ) : (
          <div className="space-y-2 min-w-[280px]">
            <input
              className="kb-input w-full text-sm"
              type="text"
              placeholder="Preferred date / time"
              value={demoForm.preferred_at}
              onChange={(e) => setDemoForm({ ...demoForm, preferred_at: e.target.value })}
              data-testid="demo-preferred-at"
            />
            <textarea
              className="kb-input w-full text-sm"
              rows={2}
              placeholder="Tell us what you'd like to focus on (optional)"
              value={demoForm.notes}
              onChange={(e) => setDemoForm({ ...demoForm, notes: e.target.value })}
              data-testid="demo-notes"
            />
            <button onClick={scheduleDemo} className="kb-btn-outline w-full inline-flex items-center justify-center gap-2" data-testid="demo-submit">
              Schedule a 15-min demo <ArrowRight size={14} />
            </button>
          </div>
        )}
      </section>

      <div className="text-center">
        <Link to="/admin" className="text-xs text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))]">← Back to admin</Link>
      </div>
    </div>
  );
}
